import routesPaths from 'constants/routesPaths';
import {
    admin,
    moderationWithMagicLink,
    moderationWithMagicLinkTokenKey,
} from 'utils/constants';
import { clearLastRoute } from 'utils/pwaRoutePersistence';

export const INSTALL_CMS_MAGIC_LINK_PARAM = 'magic-link';
export const SAVED_MODERATOR_MAGIC_LINK_KEY = 'saved_moderator_magic_link';
export const INSTALL_CMS_SESSION_MAGIC_LINK_KEY =
    'install_cms_session_magic_link';
export const INSTALL_CMS_MAGIC_LINK_MESSAGE_TYPE =
    'COVISION_INSTALL_CMS_MAGIC_LINK';
export const INSTALL_CMS_MAGIC_LINK_ACK_TYPE =
    'COVISION_INSTALL_CMS_MAGIC_LINK_ACK';

export const buildModerationMagicLinkUrl = ({
    engagementToolId,
    instanceId,
    token,
    origin = window.location.origin,
}) => {
    const path = routesPaths.MODERATE_QA_WITH_MAGIC_LINK.replace(
        ':engagementToolId',
        engagementToolId
    ).replace(':instanceId', instanceId);

    return `${origin}${path}?${moderationWithMagicLinkTokenKey}=${token}`;
};

export const getInstallableModeratorAppUrl = () => {
    const origin = window?.location?.origin;
    return `${origin.replace(admin, '//install-cms.')}`;
};

export const getInstallCmsOrigin = () => {
    try {
        return new URL(getInstallableModeratorAppUrl()).origin;
    } catch (e) {
        return '';
    }
};

export const getCmsOriginFromInstallOrigin = (
    installOrigin = window.location.origin
) => installOrigin.replace('://install-cms.', '://cms.');

export const buildInstallCmsUrlWithMagicLink = (magicLinkUrl) =>
    `${getInstallableModeratorAppUrl()}?${INSTALL_CMS_MAGIC_LINK_PARAM}=${encodeURIComponent(
        magicLinkUrl
    )}`;

export const getMagicLinkFromInstallCmsSearch = (
    search = window.location.search
) => new URLSearchParams(search).get(INSTALL_CMS_MAGIC_LINK_PARAM) || '';

export const getSessionInstallCmsMagicLink = () =>
    sessionStorage.getItem(INSTALL_CMS_SESSION_MAGIC_LINK_KEY) || '';

export const setSessionInstallCmsMagicLink = (magicLinkUrl = '') => {
    if (!magicLinkUrl) {
        sessionStorage.removeItem(INSTALL_CMS_SESSION_MAGIC_LINK_KEY);
        return;
    }
    sessionStorage.setItem(INSTALL_CMS_SESSION_MAGIC_LINK_KEY, magicLinkUrl);
};

/**
 * Reads magic-link from the query (or session), stores it for the page session,
 * and strips the query from the address bar so only the install domain is visible.
 */
export const consumeInstallCmsMagicLinkFromUrl = () => {
    const fromQuery = getMagicLinkFromInstallCmsSearch();
    const magicLink = fromQuery || getSessionInstallCmsMagicLink();

    if (fromQuery) {
        setSessionInstallCmsMagicLink(fromQuery);
    }

    if (window.location.search) {
        window.history.replaceState(
            {},
            document.title,
            window.location.origin + window.location.pathname
        );
    }

    return magicLink;
};

/**
 * Opens clean install-cms URL and passes magic link via postMessage (never in the URL).
 */
export const openInstallCmsWithMagicLinkBehindScenes = (magicLinkUrl) => {
    if (!magicLinkUrl) {
        return false;
    }

    const installUrl = getInstallableModeratorAppUrl();
    const installOrigin = getInstallCmsOrigin();
    // Do not use noopener — we need the window reference to postMessage.
    const childWindow = window.open(installUrl, '_blank');
    if (!childWindow) {
        return false;
    }

    let attempts = 0;
    const maxAttempts = 40;
    const intervalId = window.setInterval(() => {
        attempts += 1;
        try {
            childWindow.postMessage(
                {
                    type: INSTALL_CMS_MAGIC_LINK_MESSAGE_TYPE,
                    magicLink: magicLinkUrl,
                },
                installOrigin
            );
        } catch (e) {
            // Child may not be ready yet.
        }

        if (attempts >= maxAttempts || childWindow.closed) {
            window.clearInterval(intervalId);
            window.removeEventListener('message', onAck);
        }
    }, 250);

    const onAck = (event) => {
        if (event.origin !== installOrigin) {
            return;
        }
        if (event.data?.type !== INSTALL_CMS_MAGIC_LINK_ACK_TYPE) {
            return;
        }
        window.clearInterval(intervalId);
        window.removeEventListener('message', onAck);
    };

    window.addEventListener('message', onAck);
    return true;
};

export const clearSavedModeratorMagicLink = () =>
    localStorage.removeItem(SAVED_MODERATOR_MAGIC_LINK_KEY);

export const openInstallCmsConfigurationScreen = () => {
    clearSavedModeratorMagicLink();
    clearLastRoute();
    const url = getInstallableModeratorAppUrl();
    window.location.replace(`${url}?iscleanstorage=true`);
};

export const isValidModerationMagicLink = (value = '') => {
    try {
        const url = new URL(value);
        return url.href.includes(moderationWithMagicLink);
    } catch (e) {
        return false;
    }
};

export const extractModerationMagicLinkFromScannedText = (value = '') => {
    const trimmed = String(value).trim();
    if (!trimmed) {
        return '';
    }

    if (isValidModerationMagicLink(trimmed)) {
        return new URL(trimmed).href;
    }

    try {
        const url = new URL(trimmed);
        const nestedMagicLink = url.searchParams.get(
            INSTALL_CMS_MAGIC_LINK_PARAM
        );
        if (nestedMagicLink && isValidModerationMagicLink(nestedMagicLink)) {
            return new URL(nestedMagicLink).href;
        }
    } catch (e) {
        // Fall through to regex extraction.
    }

    const matchedUrl = trimmed.match(/https?:\/\/[^\s]+/i)?.[0];
    if (matchedUrl && matchedUrl !== trimmed) {
        return extractModerationMagicLinkFromScannedText(matchedUrl);
    }

    return '';
};
